import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { PayrollEntry } from '../types';

export function usePayroll() {
  const [payrollEntries, setPayrollEntries] = useState<PayrollEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPayrollEntries = useCallback(async () => {
    try {
      // Add retry logic
      let attempts = 0;
      const maxAttempts = 3;
      let data, error;
      
      while (attempts < maxAttempts) {
        const result = await supabase
        .from('payroll_entries')
        .select(`
          id,
          employee_id,
          gross_pay,
          net_pay,
          tax_deduction,
          federal_tax,
          medicare_tax,
          social_security_tax,
          state_tax,
          insurance_deduction,
          retirement_deduction,
          pay_period_start,
          pay_period_end,
          pay_date,
          status,
          bonus,
          employees (
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });
        
        if (result.error) {
          if (attempts < maxAttempts - 1) {
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
            continue;
          }
          error = result.error;
        } else {
          data = result.data;
          break;
        }
      }

      if (error) throw error;

      console.log('usePayroll - Raw data from Supabase:', data?.length || 0, 'entries');

      setPayrollEntries((data || []).map(entry => ({
        id: entry.id,
        employeeId: entry.employee_id,
        grossPay: parseFloat(entry.gross_pay) || 0,
        netPay: parseFloat(entry.net_pay) || 0,
        deductions: {
          tax: parseFloat(entry.tax_deduction) || 0,
          federalTax: parseFloat(entry.federal_tax) || 0,
          medicareTax: parseFloat(entry.medicare_tax) || 0,
          socialSecurityTax: parseFloat(entry.social_security_tax) || 0,
          stateTax: parseFloat(entry.state_tax) || 0,
          insurance: parseFloat(entry.insurance_deduction) || 0,
          retirement: parseFloat(entry.retirement_deduction) || 0
        },
        payPeriod: {
          start: entry.pay_period_start,
          end: entry.pay_period_end
        },
        payDate: entry.pay_date || undefined,
        status: entry.status,
        bonus: entry.bonus ? parseFloat(entry.bonus) : undefined
      })));
    } catch (err) {
      console.error('usePayroll - Error fetching payroll entries:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch payroll entries. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayrollEntries();
  }, [fetchPayrollEntries]);

  return { payrollEntries, loading, error, refetch: fetchPayrollEntries };
}